import { useState } from 'react';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addStaffMember,
  grantStaffRole,
  listMcpSyncFindings,
  revokeAllPersonMcpApiKeys,
  syncMcpGroupsWithLogto,
} from '@/apps/apis/mcpAccess';
import type { McpSyncFinding, McpSyncKind } from '@/apps/apis/mcpAccess/types';
import { errorMessage } from './mcpAccessFormat';

const SYNC_QUERY_KEY = ['mcpAccess', 'sync'] as const;

const KIND_LABEL: Record<McpSyncKind, { text: string; variant: string }> = {
  leftStaff: { text: 'Left staff, keys still active', variant: 'danger' },
  groupsDiffer: { text: 'Tools differ from team roles', variant: 'warning' },
  notInLogto: { text: 'Not in Logto', variant: 'secondary' },
};

const groupList = (groups: string[] | null): string => (groups === null ? '—' : groups.join(', ') || 'none');

/**
 * Every MCP user whose access does not match Logto, for company admins, with the fixes they can choose. It serves the
 * backfill of people who got MCP access before Logto and changes made in the Logto console; it stays folded, and is
 * not asked for, until opened. Nothing changes until a button is pressed: the usual fix is to give the matching team
 * role, not to take their tools away.
 */
const McpSyncContent = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const findings = useQuery({ queryKey: SYNC_QUERY_KEY, queryFn: listMcpSyncFindings, enabled: open });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [fixError, setFixError] = useState<string | null>(null);

  const fix = async (action: () => Promise<unknown>, done: string, failure: string) => {
    setBusy(true);
    setNotice(null);
    setFixError(null);
    try {
      await action();
      setNotice(done);
    } catch (error) {
      setFixError(errorMessage(error, failure));
    } finally {
      setBusy(false);
      for (const queryKey of [SYNC_QUERY_KEY, ['mcpAccess', 'members'], ['mcpAccess', 'people']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    }
  };

  const actions = (finding: McpSyncFinding) => {
    const { email } = finding;
    if (finding.kind === 'leftStaff') {
      return (
        <Button
          size="sm"
          variant="outline-danger"
          disabled={busy}
          onClick={() =>
            void fix(
              () => revokeAllPersonMcpApiKeys(email),
              `Revoked the keys of ${email}.`,
              'Could not revoke the keys.',
            )
          }
        >
          Revoke {finding.activeKeyCount} {finding.activeKeyCount === 1 ? 'key' : 'keys'}
        </Button>
      );
    }
    const giveRole = (role: string) =>
      finding.kind === 'notInLogto'
        ? fix(() => addStaffMember(email, role), `Added ${email} with ${role}.`, `Could not add ${email}.`)
        : fix(() => grantStaffRole(email, role), `Gave ${role} to ${email}.`, 'Could not grant the role.');
    return (
      <span className="d-inline-flex flex-wrap gap-1 justify-content-end">
        {finding.suggestedRoles.map((role) => (
          <Button key={role} size="sm" variant="outline-primary" disabled={busy} onClick={() => void giveRole(role)}>
            {finding.kind === 'notInLogto' ? 'Add with' : 'Give'} {role}
          </Button>
        ))}
        {finding.kind === 'groupsDiffer' && (
          <Button
            size="sm"
            variant="outline-danger"
            disabled={busy}
            title="Sets their MCP tools to what their team roles grant; this can take tools away"
            onClick={() =>
              void fix(
                () => syncMcpGroupsWithLogto(email),
                `Set the MCP tools of ${email} from their team roles.`,
                'Could not change the MCP tools.',
              )
            }
          >
            Use team roles
          </Button>
        )}
      </span>
    );
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2 mt-4 mb-1">
        <h2 className="h5 mb-0">Admin: compare with Logto</h2>
        <Button size="sm" variant="outline-secondary" onClick={() => setOpen((wasOpen) => !wasOpen)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </div>
      <p className="text-muted small">
        For admins: MCP users whose access does not match their Logto team roles, after a backfill or a change made in
        the Logto console. Changes made on this page never need it.
      </p>
      {open && (
        <>
          <p className="text-muted small">
            Usually the fix is to give the matching team role (backfill). &quot;Use team roles&quot; takes away tools
            their roles do not grant. Nothing changes until you press a button.
          </p>
          {findings.isPending && <Spinner animation="border" size="sm" />}
          {findings.isError && (
            <Alert variant="danger">{errorMessage(findings.error, 'Could not compare with Logto.')}</Alert>
          )}
          {fixError && <Alert variant="danger">{fixError}</Alert>}
          {notice && <Alert variant="success">{notice}</Alert>}
          {findings.data && findings.data.length === 0 && (
            <Alert variant="success">Every MCP user matches Logto.</Alert>
          )}
          {findings.data && findings.data.length > 0 && (
            <Table size="sm" hover responsive>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Finding</th>
                  <th>MCP tools now</th>
                  <th>Team roles grant</th>
                  <th>
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {findings.data.map((finding) => (
                  <tr key={finding.email}>
                    <td>{finding.email}</td>
                    <td>
                      <Badge bg={KIND_LABEL[finding.kind].variant}>{KIND_LABEL[finding.kind].text}</Badge>
                    </td>
                    <td className="small">{groupList(finding.storedGroups)}</td>
                    <td className="small">{groupList(finding.logtoGroups)}</td>
                    <td className="text-end">{actions(finding)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </>
  );
};

export default McpSyncContent;
